/**
 * src/components/mobile/MobileQRScanner.jsx
 * Route: /mobile/scan
 * Uses device camera to scan QR codes and navigate to asset URL.
 * npm install jsqr  (lightweight, no WebAssembly)
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';

export default function MobileQRScanner() {
  const navigate    = useNavigate();
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const streamRef   = useRef(null);

  const [status, setStatus]   = useState('starting'); // starting | scanning | found | error
  const [result, setResult]   = useState(null);
  const [errMsg, setErrMsg]   = useState('');

  /* ── Start camera ─────────────────────────────────────────── */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        streamRef.current = stream;
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStatus('scanning');
        tick();
      } catch(e) {
        setStatus('error');
        setErrMsg(e.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access and try again.'
          : `Camera error: ${e.message}`);
      }
    })();

    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  /* ── Scan loop ─────────────────────────────────────────────── */
  const tick = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const ctx = canvas.getContext('2d');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    if (code) {
      handleFound(code.data);
    } else {
      rafRef.current = requestAnimationFrame(tick);
    }
  };

  /* ── Handle QR result ─────────────────────────────────────── */
  const handleFound = (data) => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    setStatus('found');
    setResult(data);

    /* If it's an iAssets URL, extract path and navigate */
    try {
      const url  = new URL(data);
      const path = url.pathname;      // e.g. /a/VC25A03CW0004
      if (path.startsWith('/a/')) {
        setTimeout(() => navigate(path), 600);
        return;
      }
      /* External URL — open in browser */
      window.location.href = data;
    } catch {
      /* Not a URL — just show the result */
    }
  };

  const retry = () => {
    setStatus('starting');
    setResult(null);
    setErrMsg('');
    /* Re-mount by reloading */
    window.location.reload();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'#000', zIndex:500,
      display:'flex', flexDirection:'column',
      fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* Top bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, zIndex:10,
        padding:'max(env(safe-area-inset-top,0px),16px) 16px 14px',
        display:'flex', alignItems:'center', gap:12,
        background:'linear-gradient(to bottom, rgba(0,0,0,.7), transparent)' }}>
        <button onClick={() => navigate(-1)}
          style={{ background:'rgba(255,255,255,.15)', border:'none',
            borderRadius:'50%', width:38, height:38, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'#fff', backdropFilter:'blur(4px)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={{ color:'#fff', fontSize:16, fontWeight:700 }}>
          Scan Asset QR Code
        </span>
      </div>

      {/* Camera feed */}
      <video ref={videoRef} playsInline muted
        style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
      <canvas ref={canvasRef} style={{ display:'none' }}/>

      {/* Viewfinder overlay */}
      {status === 'scanning' && (
        <div style={{ position:'absolute', inset:0, display:'flex',
          alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
          {/* Corner brackets */}
          <div style={{ position:'relative', width:220, height:220 }}>
            {[
              { top:0,   left:0,   borderTop:'3px solid #fff', borderLeft:'3px solid #fff' },
              { top:0,   right:0,  borderTop:'3px solid #fff', borderRight:'3px solid #fff' },
              { bottom:0,left:0,   borderBottom:'3px solid #fff', borderLeft:'3px solid #fff' },
              { bottom:0,right:0,  borderBottom:'3px solid #fff', borderRight:'3px solid #fff' },
            ].map((s,i) => (
              <div key={i} style={{ position:'absolute', width:28, height:28,
                borderRadius:3, ...s }}/>
            ))}
            {/* Scan line animation */}
            <div style={{ position:'absolute', left:0, right:0, height:2,
              background:'rgba(26,86,219,.8)',
              animation:'scanline 2s linear infinite',
              boxShadow:'0 0 8px rgba(26,86,219,.8)' }}/>
          </div>
          <style>{`
            @keyframes scanline {
              0%   { top: 0; }
              50%  { top: calc(100% - 2px); }
              100% { top: 0; }
            }
          `}</style>
        </div>
      )}

      {/* Bottom hint */}
      {status === 'scanning' && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, zIndex:10,
          padding:'16px 24px max(env(safe-area-inset-bottom,0px),24px)',
          background:'linear-gradient(to top, rgba(0,0,0,.7), transparent)',
          textAlign:'center' }}>
          <p style={{ color:'rgba(255,255,255,.8)', fontSize:13, margin:0 }}>
            Point the camera at an iAssets QR code
          </p>
        </div>
      )}

      {/* Starting spinner */}
      {status === 'starting' && (
        <div style={{ position:'absolute', inset:0, display:'flex',
          flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
          <div style={{ width:40, height:40, border:'3px solid rgba(255,255,255,.2)',
            borderTopColor:'#fff', borderRadius:'50%',
            animation:'spin .8s linear infinite' }}/>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ color:'rgba(255,255,255,.7)', fontSize:13 }}>Starting camera…</span>
        </div>
      )}

      {/* Found state */}
      {status === 'found' && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.85)',
          display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'center', gap:16, padding:24 }}>
          <div style={{ fontSize:56 }}>✅</div>
          <span style={{ color:'#fff', fontSize:16, fontWeight:700 }}>QR Code Found!</span>
          <span style={{ color:'rgba(255,255,255,.6)', fontSize:12,
            wordBreak:'break-all', textAlign:'center' }}>{result}</span>
          <span style={{ color:'rgba(255,255,255,.5)', fontSize:13 }}>Opening asset…</span>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div style={{ position:'absolute', inset:0, background:'#1a1f2e',
          display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'center', gap:16, padding:32 }}>
          <div style={{ fontSize:48 }}>📷</div>
          <span style={{ color:'#fff', fontSize:16, fontWeight:700, textAlign:'center' }}>
            Camera Unavailable
          </span>
          <span style={{ color:'rgba(255,255,255,.6)', fontSize:13,
            textAlign:'center', lineHeight:1.6 }}>{errMsg}</span>
          <button onClick={retry}
            style={{ marginTop:8, padding:'12px 28px',
              background:'#1a56db', color:'#fff', border:'none',
              borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Try Again
          </button>
          <button onClick={() => navigate(-1)}
            style={{ padding:'10px 20px', background:'transparent',
              color:'rgba(255,255,255,.6)', border:'1px solid rgba(255,255,255,.2)',
              borderRadius:10, fontSize:13, cursor:'pointer' }}>
            Go Back
          </button>
        </div>
      )}
    </div>
  );
}
